import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'flag-icons/css/flag-icons.min.css';
import './index.css';

import { AuthProvider, useAuth }           from './context/AuthContext';
import { ThemeProvider }                   from './context/ThemeContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { ToastProvider }                   from './components/UI';

import Layout          from './components/Layout';
import StudentLayout   from './components/StudentLayout';
import TrainerLayout   from './components/TrainerLayout';
import FinanceLayout   from './components/FinanceLayout';
import LoginPage       from './pages/LoginPage';
import PasswordRecoveryPage from './pages/PasswordRecoveryPage';
import DashboardPage   from './pages/DashboardPage';
import InsightsPage    from './pages/InsightsPage';
import StudentsPage    from './pages/StudentsPage';
import AcademicPage    from './pages/AcademicPage';
import PermissionsPage from './pages/PermissionsPage';
import SettingsPage    from './pages/SettingsPage';
import ProgressionReportPage from './pages/ProgressionReportPage';
import ClassPassingRatePage from './pages/ClassPassingRatePage';
import ESurveyLinksPage from './pages/ESurveyLinksPage';
import {
  StudentDashboardPage, StudentAttendancePage, StudentAcademicPage,
  StudentActivitiesPage, StudentRequestsPage, StudentProfilePage,
} from './pages/student';
import {
  TrainerDashboardPage, TrainerSchedulesPage, TrainerObservationPage,
  TrainerAssessmentPage, TrainerComponentMarksPage, TrainerClaimPage, TrainerProfilePage,
} from './pages/trainer';
import FinanceClaimsPage from './pages/finance/FinanceClaimsPage';

import {
  IntakesPage, CourseLevelsPage, CoursesPage, ModulesPage, ComponentMasterPage, MarksReviewPage, AttendancePage, DisciplinaryPage,
  AlumniPage, RequestsPage, LogActivitiesPage, ReceiptsPage,
} from './pages/RecordPages';
import { AuditLogsPage, UsersPage, ApprovalSettingsPage } from './pages/SystemPages';
import TaeppQueuePage from './pages/TaeppQueuePage';
import {
  TrainerDirectoryPage, TrainerClaimsQueuePage, TrainerObservationsPage, TrainerAssessmentsQueuePage,
  TrainerFeeRatesPage, TrainerMarkingPermissionsPage, TrainerScheduleBoardPage,
} from './pages/TrainerAdminPages';

// Shown when a logged-in user navigates to a module they don't have view access to
function NoPermission() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center no-perm-wrap"
      style={{ minHeight: '55vh', padding: 24 }}>
      <div className="no-perm-ring-wrap">
        <span className="no-perm-ring" aria-hidden="true" />
        <span className="no-perm-ring delay" aria-hidden="true" />
        <div className="no-perm-circle">
          <i className="bi bi-shield-lock-fill" style={{ fontSize: 28, color: 'var(--sms-acc)' }} aria-hidden="true" />
        </div>
      </div>
      <h5 style={{ color: 'var(--sms-txt)', fontWeight: 600, marginBottom: 8 }}>No Permission</h5>
      <p style={{ color: 'var(--sms-txt2)', fontSize: 14, maxWidth: 380, marginBottom: 20 }}>
        You don't have access to view this page. Contact your administrator if you believe this is a mistake.
      </p>
      <Link to="/" className="sms-btn sms-btn-primary">
        <i className="bi bi-house" /> Go to Dashboard
      </Link>
    </div>
  );
}

// Guard: must be logged in
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

// Guard: must be super_admin
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                         return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin')   return <Navigate to="/"      replace />;
  return children;
}

// Guard: must belong to a department (the server re-checks users.dept_code)
function DeptRoute({ dept, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                    return <Navigate to="/login" replace />;
  if (user.dept_code !== dept)  return <Navigate to="/"      replace />;
  return children;
}

// Guard: must have view permission for module
function PermRoute({ module, children }) {
  const { user, loading } = useAuth();
  const { canView, loaded } = usePermissions();
  if (loading || !loaded) return null;
  if (!user)              return <Navigate to="/login" replace />;
  if (!canView(module))   return <Layout><NoPermission /></Layout>;
  return children;
}

// Guard: must be a logged-in student — staff/trainer accounts are sent back to the staff dashboard
function StudentRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                    return <Navigate to="/login" replace />;
  if (user.role !== 'student')  return <Navigate to="/"      replace />;
  return children;
}

// Guard: must be a logged-in trainer — other roles are sent to their own home
function TrainerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                   return <Navigate to="/login" replace />;
  if (user.role !== 'trainer') return <Navigate to={user.role === 'student' ? '/student' : '/'} replace />;
  return children;
}

// Guard: must be a logged-in finance user — other roles are sent to their own home.
function FinanceRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)                   return <Navigate to="/login" replace />;
  if (user.role !== 'finance') return <Navigate to={homeFor(user)} replace />;
  return children;
}

// Where a logged-in user lands after /login or when hitting a route that isn't theirs
function homeFor(user) {
  if (user?.role === 'student') return '/student';
  if (user?.role === 'trainer') return '/trainer';
  if (user?.role === 'finance') return '/finance';
  return '/';
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homeFor(user)} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to={homeFor(user)} replace /> : <PasswordRecoveryPage />} />
      <Route path="/reset-password" element={user ? <Navigate to={homeFor(user)} replace /> : <PasswordRecoveryPage reset />} />

      {/* Dashboard */}
      <Route path="/" element={
        <PrivateRoute>
          {user?.role === 'student' || user?.role === 'trainer' || user?.role === 'finance' ? <Navigate to={homeFor(user)} replace /> : <Layout><DashboardPage /></Layout>}
        </PrivateRoute>
      } />

      {/* Finance claims-for-payment self-service */}
      <Route path="/finance" element={<FinanceRoute><FinanceLayout><FinanceClaimsPage /></FinanceLayout></FinanceRoute>} />

      <Route path="/insights" element={<PrivateRoute><Layout><InsightsPage /></Layout></PrivateRoute>} />

      {/* Student self-service portal */}
      <Route path="/student"             element={<StudentRoute><StudentLayout><StudentDashboardPage /></StudentLayout></StudentRoute>} />
      <Route path="/student/attendance"  element={<StudentRoute><StudentLayout><StudentAttendancePage /></StudentLayout></StudentRoute>} />
      <Route path="/student/academic"    element={<StudentRoute><StudentLayout><StudentAcademicPage /></StudentLayout></StudentRoute>} />
      <Route path="/student/activities"  element={<StudentRoute><StudentLayout><StudentActivitiesPage /></StudentLayout></StudentRoute>} />
      <Route path="/student/requests"    element={<StudentRoute><StudentLayout><StudentRequestsPage /></StudentLayout></StudentRoute>} />
      <Route path="/student/profile"     element={<StudentRoute><StudentLayout><StudentProfilePage /></StudentLayout></StudentRoute>} />

      {/* Trainer self-service portal */}
      <Route path="/trainer"             element={<TrainerRoute><TrainerLayout><TrainerDashboardPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/schedules"   element={<TrainerRoute><TrainerLayout><TrainerSchedulesPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/observation" element={<TrainerRoute><TrainerLayout><TrainerObservationPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/assessment"  element={<TrainerRoute><TrainerLayout><TrainerAssessmentPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/component-marks" element={<TrainerRoute><TrainerLayout><TrainerComponentMarksPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/claim"       element={<TrainerRoute><TrainerLayout><TrainerClaimPage /></TrainerLayout></TrainerRoute>} />
      <Route path="/trainer/profile"     element={<TrainerRoute><TrainerLayout><TrainerProfilePage /></TrainerLayout></TrainerRoute>} />

      {/* Students */}
      <Route path="/students/profile" element={<PermRoute module="students"><Layout><StudentsPage /></Layout></PermRoute>} />
      <Route path="/students/attendance" element={<PermRoute module="attendance"><Layout><AttendancePage /></Layout></PermRoute>} />
      <Route path="/students/academic"   element={<PermRoute module="academic_performance"><Layout><AcademicPage /></Layout></PermRoute>} />
      <Route path="/students/activities" element={<PermRoute module="activities"><Layout><LogActivitiesPage /></Layout></PermRoute>} />

      {/* Reports */}
      <Route path="/reports/progression" element={<PermRoute module="reports"><Layout><ProgressionReportPage /></Layout></PermRoute>} />
      <Route path="/reports/passing-rate" element={<PermRoute module="reports"><Layout><ClassPassingRatePage /></Layout></PermRoute>} />

      {/* Records */}
      <Route path="/records/intakes"      element={<PermRoute module="intake_master"><Layout><IntakesPage /></Layout></PermRoute>} />
      <Route path="/records/course-levels" element={<PermRoute module="course_level_master"><Layout><CourseLevelsPage /></Layout></PermRoute>} />
      <Route path="/records/courses"      element={<PermRoute module="course_master"><Layout><CoursesPage /></Layout></PermRoute>} />
      <Route path="/records/modules"      element={<PermRoute module="module_master"><Layout><ModulesPage /></Layout></PermRoute>} />
<Route path="/records/components"   element={<PermRoute module="component_master"><Layout><ComponentMasterPage /></Layout></PermRoute>} />
      <Route path="/records/marks-review" element={<PermRoute module="marks_review"><Layout><MarksReviewPage /></Layout></PermRoute>} />
      <Route path="/records/marking-permissions" element={<PermRoute module="trainer_marking_permissions"><Layout><TrainerMarkingPermissionsPage /></Layout></PermRoute>} />
      <Route path="/records/esurvey-links" element={<PermRoute module="survey_links"><Layout><ESurveyLinksPage /></Layout></PermRoute>} />
      <Route path="/records/disciplinary" element={<PermRoute module="disciplinary"><Layout><DisciplinaryPage /></Layout></PermRoute>} />
      <Route path="/records/alumni"       element={<PermRoute module="alumni"><Layout><AlumniPage /></Layout></PermRoute>} />
      <Route path="/records/requests"     element={<PermRoute module="requests"><Layout><RequestsPage /></Layout></PermRoute>} />

      {/* Trainers */}
      <Route path="/trainers/schedule"     element={<PermRoute module="trainer_schedule"><Layout><TrainerScheduleBoardPage /></Layout></PermRoute>} />
      <Route path="/trainers/directory"    element={<PermRoute module="trainer_directory"><Layout><TrainerDirectoryPage /></Layout></PermRoute>} />
      <Route path="/trainers/claims"       element={<PermRoute module="trainer_claims_queue"><Layout><TrainerClaimsQueuePage /></Layout></PermRoute>} />
      <Route path="/trainers/assessments"  element={<PermRoute module="trainer_assessments"><Layout><TrainerAssessmentsQueuePage /></Layout></PermRoute>} />
      <Route path="/trainers/observations" element={<PermRoute module="trainer_observations"><Layout><TrainerObservationsPage /></Layout></PermRoute>} />
      <Route path="/trainers/fee-rates"    element={<PermRoute module="trainer_fee_rates"><Layout><TrainerFeeRatesPage /></Layout></PermRoute>} />

      {/* Payment */}
      <Route path="/payment/receipts" element={<PermRoute module="receipts"><Layout><ReceiptsPage /></Layout></PermRoute>} />

      {/* System */}
      <Route path="/system/audit-logs"   element={<PermRoute module="audit_logs"><Layout><AuditLogsPage /></Layout></PermRoute>} />
      <Route path="/system/users"        element={<SuperAdminRoute><Layout><UsersPage /></Layout></SuperAdminRoute>} />
      <Route path="/system/permissions"  element={<SuperAdminRoute><Layout><PermissionsPage /></Layout></SuperAdminRoute>} />
      <Route path="/trainers/taepp" element={<PrivateRoute><Layout><TaeppQueuePage /></Layout></PrivateRoute>} />
      <Route path="/system/approval-settings" element={<DeptRoute dept="DEP-09"><Layout><ApprovalSettingsPage /></Layout></DeptRoute>} />
      <Route path="/system/taepp-approver" element={<Navigate to="/system/approval-settings" replace />} />
      <Route path="/system/claim-verifiers" element={<Navigate to="/system/approval-settings" replace />} />
      <Route path="/system/settings"     element={<PrivateRoute><Layout><SettingsPage /></Layout></PrivateRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <PermissionsProvider>
              <AppRoutes />
            </PermissionsProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
